import {
  calculateInvoiceDates,
  calculateInvoiceMonth,
  calculateInvoiceStatus,
  getCreditCardInvoiceStatusLabel as getCreditCardInvoiceStatusLabelFromEngine,
  getInstallmentAmounts as getCreditCardInstallmentAmounts,
  shiftMonth as shiftCreditCardMonth,
  type CreditCardInvoiceStatus,
  type CreditCardAmountMode,
} from "@/lib/creditCardEngine";

const ENTRY_TYPE_LABELS: Record<string, string> = {
  income: "Receita",
  expense: "Despesa",
  investment: "Investimento",
  debt: "Dívida",
  transfer: "Transferência",
};

export type InstallmentAmountMode = CreditCardAmountMode;
export type { CreditCardInvoiceStatus };
export type SharedFinancialEntryLike = Record<string, unknown>;

type ExpenseLike = {
  expense_month?: string | null;
  created_at?: string | null;
  recurrence_type?: "single" | "recurring" | "installment" | null;
};

export function getEntryTypeLabel(type: string) {
  return ENTRY_TYPE_LABELS[type] ?? type;
}

export function normalizeCategoryValue(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function formatBrl(value: number) {
  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

export function formatChartCurrency(value: number) {
  if (value < 0) return `-${formatBrl(Math.abs(value))}`;
  return formatBrl(value);
}

export function clampPercentage(value: number) {
  if (!Number.isFinite(value)) return 0;
  return Math.min(Math.max(value, 0), 100);
}

export function formatDate(value: string) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map((part) => Number(part));
    return new Date(year, month - 1, day).toLocaleDateString("pt-BR");
  }

  return new Date(value).toLocaleDateString("pt-BR");
}

export function formatMonthLabel(value: string) {
  const [year, month] = value.split("-").map(Number);
  const date = new Date(year, month - 1);

  return date.toLocaleDateString("pt-BR", {
    month: "long",
    year: "numeric",
  });
}

export function shiftMonth(value: string, amount: number) {
  return shiftCreditCardMonth(value, amount);
}

export function getCreditCardInvoiceMonth(
  purchaseDate: string,
  closingDay: number,
  dueDay?: number
) {
  return calculateInvoiceMonth(purchaseDate, closingDay, dueDay);
}

export function getInstallmentAmounts(
  rawAmount: number,
  installments: number,
  amountMode: InstallmentAmountMode
) {
  return getCreditCardInstallmentAmounts(rawAmount, installments, amountMode);
}

export function getInstallmentTotal(
  rawAmount: number,
  installments: number,
  amountMode: InstallmentAmountMode
) {
  return getInstallmentAmounts(rawAmount, installments, amountMode).reduce(
    (sum, amount) => Number((sum + amount).toFixed(2)),
    0
  );
}

export function getCreditCardInvoiceDates(
  invoiceMonth: string,
  closingDay: number,
  dueDay: number
) {
  return calculateInvoiceDates(invoiceMonth, closingDay, dueDay);
}

export function getCreditCardInvoiceStatusValue(
  invoiceMonth: string,
  closingDay: number,
  dueDay: number
): CreditCardInvoiceStatus {
  return calculateInvoiceStatus(invoiceMonth, closingDay, dueDay);
}

export function getCreditCardInvoiceStatusLabel(status: CreditCardInvoiceStatus) {
  return getCreditCardInvoiceStatusLabelFromEngine(status);
}

/**
 * @deprecated Use getResolvedInvoiceStatus from creditCardEngine for credit card invoices.
 */
export function getInvoiceStatus(invoiceMonth: string, dueDay: number) {
  const today = new Date();
  const [year, month] = invoiceMonth.split("-").map(Number);
  const lastDay = new Date(year, month, 0).getDate();
  const dueDate = new Date(year, month - 1, Math.min(dueDay, lastDay), 23, 59);
  const closingDate = new Date(year, month - 1, 1);

  if (today > dueDate) return "Vencida";
  if (today >= closingDate) return "Aberta";
  return "Fechada";
}

export function getLastSixMonths(value: string) {
  return Array.from({ length: 6 }, (_, index) => shiftMonth(value, index - 5));
}

export function formatShortMonthLabel(value: string) {
  const [year, month] = value.split("-").map(Number);
  const date = new Date(year, month - 1);

  return date.toLocaleDateString("pt-BR", {
    month: "short",
    year: "2-digit",
  });
}

export function getSharedEntryType(entry: SharedFinancialEntryLike) {
  return String(
    entry.type ?? entry.entry_type ?? entry.kind ?? entry.category_type ?? ""
  ).toLowerCase();
}

export function getSharedEntryAmount(entry: SharedFinancialEntryLike) {
  return Number(entry.amount ?? entry.amount_brl ?? entry.value ?? 0);
}

export function getSharedEntryCategory(entry: SharedFinancialEntryLike) {
  return String(entry.category ?? entry.sub_category ?? "Sem categoria");
}

export function getSharedEntryDescription(entry: SharedFinancialEntryLike) {
  const description = entry.description ?? entry.notes;
  return description ? String(description) : "";
}

export function getSharedEntryDate(entry: SharedFinancialEntryLike) {
  const date = entry.entry_date ?? entry.date ?? entry.created_at;
  return date ? String(date) : "";
}

export function getSharedEntryId(entry: SharedFinancialEntryLike) {
  return entry.id ? String(entry.id) : "";
}

export function getMonthDateRange(value: string) {
  const [year, month] = value.split("-").map(Number);
  const lastDay = new Date(year, month, 0).getDate();

  return {
    start: `${value}-01`,
    end: `${value}-${String(lastDay).padStart(2, "0")}`,
  };
}

export function getExpenseFrequencyStep(
  frequency: "monthly" | "yearly" | "custom",
  intervalMonths: number
) {
  if (frequency === "yearly") return 12;
  if (frequency === "custom") return intervalMonths;
  return 1;
}

export function getExpenseSeriesLabel(expense: ExpenseLike) {
  if (expense.recurrence_type === "installment") {
    return "Parcelado";
  }

  if (expense.recurrence_type === "recurring") {
    return "Recorrente";
  }

  return null;
}

export function getCalendarDateLabel(value: string) {
  const date = new Date(`${value.slice(0, 10)}T12:00:00`);
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);

  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const dayMonth = date
    .toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })
    .replace(".", "")
    .toUpperCase();

  if (sameDay(date, today)) return `HOJE — ${dayMonth}`;
  if (sameDay(date, tomorrow)) return `AMANHÃ — ${dayMonth}`;
  return dayMonth;
}

export function getExpenseCalendarDate(expense: ExpenseLike) {
  const rawMonth = String(expense.expense_month ?? "");
  if (/^\d{4}-\d{2}-\d{2}$/.test(rawMonth)) return rawMonth;
  if (/^\d{4}-\d{2}$/.test(rawMonth)) return `${rawMonth}-01`;
  return expense.created_at?.slice(0, 10) ?? new Date().toISOString().slice(0, 10);
}
